import { useMemo } from 'react'
import { featureCollection } from '@turf/helpers'
import { ControllerFieldState } from 'react-hook-form'
import { Section } from '@common/Section'
import { Button } from '@common/material'
import { Text } from '@fundamentals'
import {
  formatAreaSize,
  TimelineMap,
  PolygonEastingNorthingList,
} from '@modules/geolocation'
import { GeoLocationArea } from '@core/entities/map'
import { localizedStrings } from '@core/strings'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import { geoLocationToPolygon } from '@core/utils/geolocation-helpers'
import area from '@turf/area'

type ProjectMapGeoDrawAreaCardProps = {
  projectId: number
  geoLocationAreas: GeoLocationArea[]
  readOnly?: boolean
  fieldState?: ControllerFieldState
  onClick: () => void
}

export const ProjectMapGeoDrawAreaCard: React.FC<
  ProjectMapGeoDrawAreaCardProps
> = ({ projectId, geoLocationAreas, readOnly, fieldState, onClick }) => {
  const featurePolygon = useMemo(() => {
    if (!geoLocationAreas) return undefined

    const featurePolygons = geoLocationAreas.map((area) => {
      const featurePolygon = geoLocationToPolygon([area])
      featurePolygon.properties.selected = false

      return featurePolygon
    })

    return featureCollection(featurePolygons)
  }, [geoLocationAreas])

  const calculatedArea = useMemo(() => {
    if (!featurePolygon) return null

    const squareMeters = Math.round(area(featurePolygon))

    return formatAreaSize(squareMeters)
  }, [featurePolygon])

  return (
    <Section
      label={localizedStrings.pointOfWorks}
      labelColor='text.secondary'
      sx={{
        mr: 1,
        mt: 2,
        mb: !readOnly ? 3 : 1,
        width: '100%',
      }}
    >
      {!readOnly && (!geoLocationAreas || geoLocationAreas.length === 0) && (
        <Button
          color={fieldState.error ? 'error' : undefined}
          startIcon={<LocationOnIcon />}
          sx={{ mr: 1, mt: 1 }}
          variant='outlined'
          fullWidth
          size='large'
          onClick={onClick}
        >
          {localizedStrings.drawOnMap}
        </Button>
      )}

      {featurePolygon && featurePolygon?.features?.length !== 0 && (
        <TimelineMap
          projectId={projectId}
          featurePolygon={featurePolygon}
          style={{ height: 350, marginTop: 8, cursor: 'pointer' }}
          interactive={false}
          enableNavigationControl={false}
          onClick={onClick}
        />
      )}

      {!!calculatedArea && (
        <Text
          color='text.secondary'
          fontSize={14}
          mt={1}
          justifySelf='flex-end'
        >
          {localizedStrings.getTotalDrawnAreaSize(
            calculatedArea.displayValue,
            calculatedArea.unitName,
          )}
        </Text>
      )}

      <PolygonEastingNorthingList featurePolygon={featurePolygon} />

      <Text color='error' fontSize={12} ml={2} mt={1}>
        {fieldState?.error?.message}
      </Text>
    </Section>
  )
}
